import "./polyfill";
import { I18nextProvider } from "react-i18next";
import { i18n } from "./i18n";
import { AppLayout, ErrorFallbackMessage, WithLoadingIndicator } from "./components";
import { AppProvider, CalculatorEnv } from "./state";
import { sanitizeUrl } from "@braintree/sanitize-url";
import { parseBooleanAttribute, removeTrailingSlash } from "./api";
import { JSX } from "react";
import { ErrorBoundary } from "@sentry/react";
import { getSentryScope } from "./tracking";

type AppProps = {
  apiUrl?: string;
  signUpUrl?: string;
  ctaUrl?: string;
  calculatorEnv?: CalculatorEnv;
  showHeader?: string;
  showBudgetCalculator?: string;
};

const App = (props: AppProps): JSX.Element => {
  const { apiUrl = "", signUpUrl = "", ctaUrl = "", calculatorEnv = "agpss", showHeader, showBudgetCalculator } = props;

  // only sanitize urls if it is defined. otherwise it will return about:blank
  const sanitizedApiUrl = apiUrl && removeTrailingSlash(sanitizeUrl(apiUrl));
  const sanitizedSignUpUrl = signUpUrl && sanitizeUrl(signUpUrl);
  const sanitizedCtaUrl = ctaUrl && sanitizeUrl(ctaUrl);

  const parsedShowHeader = parseBooleanAttribute(showHeader);
  const parsedShowBudgetCalculator = parseBooleanAttribute(showBudgetCalculator);

  return (
    <ErrorBoundary
      onError={(error, componentStack, eventId) => {
        const sentryScope = getSentryScope();
        sentryScope?.setExtra("componentStack", componentStack);
        sentryScope?.captureException(error, { event_id: eventId });
      }}
      fallback={<ErrorFallbackMessage signUpUrl={sanitizedSignUpUrl} />}
    >
      <AppProvider
        apiUrl={sanitizedApiUrl}
        ctaUrl={sanitizedCtaUrl}
        calculatorEnv={calculatorEnv}
        showHeader={parsedShowHeader}
        showBudgetCalculator={parsedShowBudgetCalculator}
      >
        <I18nextProvider i18n={i18n}>
          <WithLoadingIndicator>
            <AppLayout />
          </WithLoadingIndicator>
        </I18nextProvider>
      </AppProvider>
    </ErrorBoundary>
  );
};

export default App;
